import React, { useState, useEffect } from 'react';
import { Calendar, Users, MapPin, Search, Send, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useSettings } from '../context/SettingsContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import SearchableLocation from './SearchableLocation';
import QuoteResults from './QuoteResults';
import api from '../lib/api';
import { toast } from 'sonner';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1509233725247-49e657c54213?w=1920&q=80';

const HeroSection = () => {
  const { t, lang } = useLanguage();
  const { settings } = useSettings();
  const [airports, setAirports] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [pickup, setPickup] = useState('');
  const [destination, setDestination] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [pax, setPax] = useState('2');
  const [tripType, setTripType] = useState('one-way');
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [selectedVehicleKey, setSelectedVehicleKey] = useState(null);
  const [contact, setContact] = useState({ full_name: '', email: '', whatsapp: '', comments: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/airports').then(r => setAirports(r.data)).catch(() => {});
    api.get('/hotels').then(r => setHotels(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!pickup || !destination) {
      setQuote(null);
      setSelectedVehicleKey(null);
      return;
    }
    setQuoteLoading(true);
    const timer = setTimeout(() => {
      api.post('/quote', {
        pickup, destination,
        passengers: parseInt(pax || '2', 10),
        trip_type: tripType,
      }).then(r => {
        setQuote(r.data);
        setQuoteLoading(false);
      }).catch(() => {
        setQuoteLoading(false);
        setQuote(null);
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [pickup, destination, pax, tripType]);

  const handleSelectVehicle = (q) => {
    setSelectedVehicleKey(q.vehicle_key);
    // Smooth scroll the booking widget if needed
    setTimeout(() => {
      const el = document.getElementById('hero-contact-form');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  };

  const selectedQuote = quote?.quotes?.find(q => q.vehicle_key === selectedVehicleKey);
  const paxOptions = [1,2,3,4,5,6,7,8,9,10,11,12,15,20,25,30];

  const updateContact = (k, v) => setContact(c => ({ ...c, [k]: v }));

  const submitReservation = async (e) => {
    e.preventDefault();
    if (!contact.full_name || !contact.email || !contact.whatsapp || !date) {
      toast.error(lang === 'es' ? 'Completa los campos requeridos' : 'Please complete required fields');
      return;
    }
    setSubmitting(true);
    try {
      const priceMsg = selectedQuote ? ` | Precio estimado: $${selectedQuote.price_total} USD` : '';
      const vehicleLabel = selectedQuote?.label || selectedVehicleKey;
      const payload = {
        type: 'transfer',
        full_name: contact.full_name,
        email: contact.email,
        whatsapp: contact.whatsapp,
        trip_type: tripType,
        pickup,
        destination,
        excursion_id: '',
        excursion_title: '',
        date,
        time,
        passengers: parseInt(pax || '2', 10),
        vehicle: vehicleLabel,
        comments: `${contact.comments}${priceMsg}`,
      };
      await api.post('/reservations', payload);
      toast.success(lang === 'es' ? '¡Reserva enviada! Te contactaremos por WhatsApp.' : 'Booking sent! We will contact you via WhatsApp.');
      const num = (settings && settings.whatsapp_number) || '18099638198';
      const tt = tripType === 'round-trip' ? (lang === 'es' ? 'Ida y Vuelta' : 'Round Trip') : (lang === 'es' ? 'Solo Ida' : 'One Way');
      const msg = lang === 'es'
        ? `Hola TTS Santos, quiero reservar un traslado de *${pickup}* a *${destination}*. Vehículo: *${vehicleLabel}*. Nombre: ${contact.full_name}, Fecha: ${date} ${time || ''}, Pasajeros: ${pax}, Tipo: ${tt}.${selectedQuote ? ` Precio: $${selectedQuote.price_total} USD.` : ''}`
        : `Hello TTS Santos, I want to book a transfer from *${pickup}* to *${destination}*. Vehicle: *${vehicleLabel}*. Name: ${contact.full_name}, Date: ${date} ${time || ''}, Passengers: ${pax}, Type: ${tt}.${selectedQuote ? ` Price: $${selectedQuote.price_total} USD.` : ''}`;
      window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, '_blank');
      setContact({ full_name: '', email: '', whatsapp: '', comments: '' });
      setSelectedVehicleKey(null);
      setQuote(null);
      setPickup(''); setDestination(''); setDate(''); setTime('');
    } catch {
      toast.error(lang === 'es' ? 'Error al enviar' : 'Error sending');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
      <div className="absolute inset-0">
        <img src={HERO_IMAGE} alt="Punta Cana" className="w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
        <div className="max-w-4xl">
          <div className="inline-block px-4 py-1.5 mb-6 border border-[#ff6b1a]/40 rounded-full bg-[#ff6b1a]/10 backdrop-blur-sm animate-fadeUp">
            <span className="text-[#ff6b1a] text-sm font-semibold tracking-wider uppercase">{t.hero.tag}</span>
          </div>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white leading-tight mb-6 animate-fadeUp" style={{animationDelay:'0.1s'}}>
            {t.hero.title1}
            <span className="block text-gradient-orange">{t.hero.title2}</span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl animate-fadeUp" style={{animationDelay:'0.2s'}}>{t.hero.subtitle}</p>
        </div>

        {/* Booking widget */}
        <div className="glass-card rounded-2xl p-6 md:p-8 max-w-6xl shadow-2xl animate-fadeUp" style={{animationDelay:'0.3s'}}>
          {/* Trip type tabs */}
          <div className="flex gap-2 p-1 bg-black/40 rounded-lg w-fit mb-5">
            <button type="button" onClick={() => setTripType('one-way')} className={`px-5 py-1.5 rounded-md text-xs font-semibold transition-colors ${tripType === 'one-way' ? 'bg-[#ff6b1a] text-white' : 'text-white/70 hover:text-white'}`}>{lang === 'es' ? 'Solo Ida' : 'One Way'}</button>
            <button type="button" onClick={() => setTripType('round-trip')} className={`px-5 py-1.5 rounded-md text-xs font-semibold transition-colors ${tripType === 'round-trip' ? 'bg-[#ff6b1a] text-white' : 'text-white/70 hover:text-white'}`}>{lang === 'es' ? 'Ida y Vuelta' : 'Round Trip'}</button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-2 flex items-center gap-1">
                <MapPin className="w-3 h-3 text-[#ff6b1a]" /> {t.hero.pickup}
              </label>
              <SearchableLocation value={pickup} onChange={setPickup} airports={airports} hotels={hotels} placeholder={t.hero.pickupPh} />
            </div>
            <div>
              <label className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-2 flex items-center gap-1">
                <MapPin className="w-3 h-3 text-[#ff6b1a]" /> {t.hero.destination}
              </label>
              <SearchableLocation value={destination} onChange={setDestination} airports={airports} hotels={hotels} placeholder={t.hero.destinationPh} />
            </div>
            <div>
              <label className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Calendar className="w-3 h-3 text-[#ff6b1a]" /> {t.hero.date}
              </label>
              <Input type="date" value={date} onChange={(e)=>setDate(e.target.value)} className="bg-black/40 border-white/10 text-white h-11" />
            </div>
            <div>
              <label className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Users className="w-3 h-3 text-[#ff6b1a]" /> {t.hero.passengers}
              </label>
              <Select value={pax} onValueChange={setPax}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border-white/10">
                  {paxOptions.map(p => <SelectItem key={p} value={String(p)} className="text-white">{p} {p === 1 ? t.hero.passengerWord : t.hero.passengersWord}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-2 block">
                {lang === 'es' ? 'Hora' : 'Time'}
              </label>
              <Input type="time" value={time} onChange={(e)=>setTime(e.target.value)} className="bg-black/40 border-white/10 text-white h-11" />
            </div>
          </div>

          {/* Quote results */}
          <QuoteResults
            quote={quote}
            loading={quoteLoading}
            onSelectVehicle={handleSelectVehicle}
            selectedKey={selectedVehicleKey}
          />

          {/* Inline contact form - shown after vehicle selection */}
          {selectedQuote && (
            <div id="hero-contact-form" className="mt-6 pt-6 border-t border-white/10 animate-fadeUp">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                <div>
                  <h3 className="text-lg font-bold text-white">
                    {lang === 'es' ? 'Completa tu reserva' : 'Complete your booking'}
                  </h3>
                  <p className="text-white/60 text-sm">{selectedQuote.label} · ${selectedQuote.price_total.toFixed(0)} USD</p>
                </div>
                <button type="button" onClick={() => setSelectedVehicleKey(null)} className="text-xs text-white/50 hover:text-white underline">
                  {lang === 'es' ? 'Cambiar vehículo' : 'Change vehicle'}
                </button>
              </div>

              <form onSubmit={submitReservation} className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-white/80 mb-1.5 block text-xs">{lang === 'es' ? 'Nombre Completo' : 'Full Name'} *</Label>
                    <Input value={contact.full_name} onChange={(e) => updateContact('full_name', e.target.value)} required className="bg-black/40 border-white/10 text-white h-11" />
                  </div>
                  <div>
                    <Label className="text-white/80 mb-1.5 block text-xs">Email *</Label>
                    <Input type="email" value={contact.email} onChange={(e) => updateContact('email', e.target.value)} required className="bg-black/40 border-white/10 text-white h-11" />
                  </div>
                  <div>
                    <Label className="text-white/80 mb-1.5 block text-xs">WhatsApp *</Label>
                    <Input value={contact.whatsapp} onChange={(e) => updateContact('whatsapp', e.target.value)} required placeholder="+1 829..." className="bg-black/40 border-white/10 text-white h-11" />
                  </div>
                </div>
                <div>
                  <Label className="text-white/80 mb-1.5 block text-xs">{lang === 'es' ? 'Comentarios (Opcional)' : 'Comments (Optional)'}</Label>
                  <Textarea value={contact.comments} onChange={(e) => updateContact('comments', e.target.value)} rows={2} className="bg-black/40 border-white/10 text-white" />
                </div>
                <Button type="submit" disabled={submitting} className="btn-orange border-0 w-full h-12 rounded-md text-base font-bold gap-2">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-5 h-5" /> {lang === 'es' ? `Confirmar Reserva — $${selectedQuote.price_total.toFixed(0)} USD` : `Confirm Booking — $${selectedQuote.price_total.toFixed(0)} USD`}</>}
                </Button>
              </form>
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-6 max-w-3xl mt-10">
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-black text-[#ff6b1a]">24/7</div>
            <div className="text-white/60 text-xs md:text-sm mt-1">{t.hero.stat1}</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-black text-[#ff6b1a]">Pro</div>
            <div className="text-white/60 text-xs md:text-sm mt-1">{t.hero.stat2}</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-black text-[#ff6b1a]">5★</div>
            <div className="text-white/60 text-xs md:text-sm mt-1">{t.hero.stat3}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
